import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchProducts, reserveInteractiveSystem } from "../lib/api";
import { useCart } from "../lib/cart";
import { Check, ShoppingBag, Sparkles, Plus, Minus, Wand2, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

// Marketing meta, including lifestyle images and "what's inside" lists.
const PRODUCT_META = {
  starter_kit: {
    badge: "Best for first-timers",
    accent: "#FFD54F",
    bg: "#FFF6D6",
    lifestyle: "/brand/lifestyle-sally.jpg", // Sally Salad shown under a clear glass plate of salad
    lifestyle_alt: "Sally Salad mat peeking through a clear plate filled with salad",
    perks: [
      "Clear play-plate (BPA-free, dishwasher safe)",
      "6 collectible Buddy mats",
      "Access to the digital meal planner",
      "Parent quick-start guide",
    ],
    headline: "The clear plate that turns dinner into a peek-a-boo adventure.",
  },
  premium_kit: {
    badge: "Most loved",
    accent: "#F26A34",
    bg: "#FFFFFF",
    lifestyle: "/brand/premium-hero.jpg",
    lifestyle_alt: "Plate Buddies Premium Kit product packaging with Queso Kid, Captain Sammy, and Sally Salad mats and magnets",
    perks: [
      "Everything in the Starter Kit",
      "Upgraded Tempered Glass Play Plate",
      "6 matching Buddy magnets for meal planning",
      "Access to the Buddy Generator",
    ],
    featured: true,
    headline: "The full system — plates, planning board, and the whole crew.",
  },
  addon_buddy: {
    badge: "Collect 'em all",
    accent: "#4DD0E1",
    bg: "#D6F4F8",
    lifestyle: "/buddies/captain-sammy.png",
    lifestyle_alt: "Captain Sammy single buddy + fridge magnet",
    perks: ["Mat + fridge magnet", "Coordinated recipe card", "Picked at checkout"],
    pickable: true,
    headline: "Single Plate Buddy + fridge magnet — pick your favorite character.",
  },
  buddy_print: {
    badge: "Personalized",
    accent: "#B39DDB",
    bg: "#ECE5F7",
    lifestyle: null,
    visual: "custom-buddy",
    lifestyle_alt: "Design your own one-of-a-kind Plate Buddy",
    perks: [
      'YOUR one-of-a-kind Buddy',
      '2.5" fridge magnet',
      "Matching Plate Buddy mat",
      "Coordinated recipe card",
    ],
    requires_buddy: true,
    headline: "Design a one-of-a-kind Buddy in your child's words — ships as a magnet + matching mat.",
  },
  interactive_system: {
    badge: "Coming soon",
    accent: "#4DD0E1",
    bg: "#E8F7FA",
    lifestyle: null,
    visual: "interactive-system",
    lifestyle_alt: "The upcoming Plate Buddies Interactive System",
    perks: [
      "Interactive, app-connected plate",
      "Guided mealtime moments with the Buddies",
      "Parent dashboard with gentle insights",
      "First-access invite for reservers",
    ],
    preorder: true,
    preorder_price: 79.99,
    headline: "An app-connected plate that turns mealtime into an interactive adventure.",
  },
};

const PICKABLE_BUDDIES = [
  { id: "pineapple-chicky", name: "Pineapple Chicky", image: "/buddies/pineapple-chicky.jpg" },
  { id: "captain-sammy", name: "Captain Sammy", image: "/buddies/captain-sammy.png" },
  { id: "maestro-meatball", name: "Maestro Meatball", image: "/buddies/maestro-meatball.jpg" },
  { id: "sally-salad", name: "Sally Salad", image: "/buddies/sally-salad.png" },
  { id: "sir-ampm", name: "Sir AM/PM", image: "/buddies/sir-ampm.png" },
  { id: "queso-kid", name: "Queso Kid", image: "/buddies/queso-kid.png" },
];

export default function Products() {
  const [products, setProducts] = useState([]);
  const [pickedBuddy, setPickedBuddy] = useState("pineapple-chicky");
  const [preorderOpen, setPreorderOpen] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts().then(setProducts).catch((err) => console.warn("[fetch] failed:", err));
  }, []);

  const handleAdd = (p) => {
    const meta = PRODUCT_META[p.id] || {};
    if (meta.requires_buddy) {
      toast.info("Make or pick a Buddy first — then add the print from your library.");
      navigate("/create");
      return;
    }
    if (meta.pickable) {
      const buddy = PICKABLE_BUDDIES.find((b) => b.id === pickedBuddy);
      addItem({
        product_id: p.id,
        name: `${buddy.name} — Plate Buddy + Fridge Magnet`,
        unit_price: p.amount,
        custom_buddy_id: buddy.id,
        custom_buddy_name: buddy.name,
        custom_buddy_image: buddy.image,
      });
      toast.success(`${buddy.name} added to cart!`);
      return;
    }
    addItem({
      product_id: p.id,
      name: p.name,
      unit_price: p.amount,
    });
    toast.success(`${p.name} added to cart!`);
  };

// Featured row: starter + premium + interactive-system pre-order.
  // Custom Buddy Print is a separate product shown in the OfferLadder, Create
  // page, and its own detail page — it does NOT belong in the kits row.
  const HERO_IDS = ["starter_kit", "premium_kit", "interactive_system"];
  const SECONDARY_IDS = [];
  const PRODUCT_DETAIL_SLUGS = {
    starter_kit: "starter-kit",
    premium_kit: "premium-kit",
    buddy_print: "custom-buddy",
  };
  // The Interactive System is pre-order-only (no price, no products collection
  // entry). Inject a synthetic product so it renders alongside the two kits.
  const INTERACTIVE_SYSTEM_PRODUCT = {
    id: "interactive_system",
    name: "Interactive Plate System",
    amount: null,
    compare_at: null,
  };
  const heroProducts = HERO_IDS
    .map((id) => {
      if (id === "interactive_system") return INTERACTIVE_SYSTEM_PRODUCT;
      return products.find((p) => p.id === id);
    })
    .filter(Boolean);
  const secondaryProducts = SECONDARY_IDS.map((id) => products.find((p) => p.id === id)).filter(Boolean);

  return (
    <section id="shop" data-testid="section-products" className="relative py-20 md:py-28 bg-pb-cream overflow-hidden scroll-mt-24">
      <div className="blob bg-pb-coral" style={{ width: 360, height: 360, top: -80, right: -80, opacity: 0.2 }} />
      <div className="blob bg-pb-sky" style={{ width: 320, height: 320, bottom: -100, left: -120, opacity: 0.4 }} />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-extrabold uppercase tracking-widest text-pb-coral">Plug &amp; play kits</p>
          <h2 className="font-display font-black text-4xl md:text-5xl mt-3 leading-tight">
            The <span className="wavy">whole system</span>, in one lovely box.
          </h2>
          <p className="mt-4 text-pb-soft text-lg">
            Prefer to skip the designing? These kits come ready to go — clear play-plate, our collectible Buddies, the meal-plan magnet board, and access to the full digital experience. For families who&rsquo;d rather unbox than build.
          </p>
        </div>

        {/* HERO product row — 3 big cards with lifestyle imagery + product detail links */}
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {heroProducts.map((p) => {
            const meta = PRODUCT_META[p.id] || {};
            const isFeatured = meta.featured;
            const isPreorder = meta.preorder;
            const slug = PRODUCT_DETAIL_SLUGS[p.id];
            return (
              <div
                key={p.id}
                data-testid={`card-product-${p.id}`}
                className={`relative rounded-[2rem] overflow-hidden border-4 flex flex-col bg-white ${
                  isFeatured
                    ? "border-pb-coral shadow-[0_30px_60px_rgba(242,106,52,0.22)] lg:-translate-y-2"
                    : isPreorder
                    ? "border-pb-aqua shadow-[0_18px_40px_rgba(77,208,225,0.25)]"
                    : "border-transparent shadow-[0_18px_40px_rgba(28,22,40,0.08)]"
                } hover:-translate-y-2 transition-all duration-300`}
              >
                {isFeatured && (
                  <span className="absolute top-4 right-4 z-10 bg-pb-coral text-white text-xs font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-full shadow inline-flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Most loved
                  </span>
                )}
                {isPreorder && (
                  <span className="absolute top-4 right-4 z-10 bg-pb-aqua text-pb-ink text-xs font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-full shadow inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Coming soon
                  </span>
                )}

                {/* Lifestyle image */}
                <div
                  className="aspect-[4/3] flex items-center justify-center relative overflow-hidden"
                  style={{ background: meta.bg || "#FFF6D6" }}
                >
                  {meta.visual === "custom-buddy" ? (
                    <CustomBuddyVisual />
                  ) : meta.visual === "interactive-system" ? (
                    <InteractiveSystemVisual />
                  ) : meta.lifestyle ? (
                    <img
                      src={meta.lifestyle}
                      alt={meta.lifestyle_alt || p.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ShoppingBag className="h-20 w-20 text-pb-ink/30" />
                  )}
                  {p.compare_at && p.compare_at > p.amount && (
                    <span className="absolute top-4 left-4 bg-white text-pb-coral text-[11px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full shadow">
                      Sale
                    </span>
                  )}
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <span
                    className="inline-block text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full text-pb-ink self-start"
                    style={{ background: meta.accent || "#FFD54F" }}
                  >
                    {meta.badge}
                  </span>
                  <h3 className="font-display font-black text-2xl mt-3 leading-tight">{p.name}</h3>
                  {meta.headline && (
                    <p className="text-pb-soft mt-2 text-sm leading-relaxed">{meta.headline}</p>
                  )}

                  {isPreorder ? (
                    <div className="mt-4 flex items-baseline gap-2 flex-wrap">
                      <span className="font-display font-black text-4xl">${meta.preorder_price?.toFixed(2)}</span>
                      <span className="text-[10px] font-extrabold uppercase tracking-widest text-pb-aqua bg-pb-aqua/15 px-2.5 py-1 rounded-full">
                        Pre-order
                      </span>
                    </div>
                  ) : (
                    <div className="mt-4 flex items-baseline gap-2">
                      <span className="font-display font-black text-4xl">${p.amount}</span>
                      {p.compare_at && p.compare_at > p.amount && (
                        <span className="text-pb-soft text-base font-bold line-through">
                          ${p.compare_at}
                        </span>
                      )}
                    </div>
                  )}

                  <ul className="mt-5 space-y-2 flex-1">
                    {(meta.perks || []).map((perk) => (
                      <li key={perk} className="flex gap-2 items-start text-sm">
                        <Check className="h-4 w-4 text-pb-coral mt-0.5 flex-shrink-0" />
                        <span className="text-pb-ink">{perk}</span>
                      </li>
                    ))}
                  </ul>

                  {meta.pickable && (
                    <BuddyPicker picked={pickedBuddy} setPicked={setPickedBuddy} />
                  )}

                  <div className="mt-6 flex flex-col gap-2">
                    {isPreorder ? (
                      <button
                        data-testid={`btn-preorder-${p.id}`}
                        onClick={() => setPreorderOpen(true)}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full font-extrabold py-3.5 transition bg-pb-ink text-white hover:bg-pb-aqua hover:text-pb-ink"
                      >
                        <Sparkles className="h-4 w-4" />
                        Reserve your spot
                      </button>
                    ) : slug ? (
                      <Link
                        to={`/products/${slug}`}
                        data-testid={`btn-details-${p.id}`}
                        className={`w-full inline-flex items-center justify-center gap-2 rounded-full font-extrabold py-3.5 transition ${
                          isFeatured
                            ? "bg-pb-coral text-white shadow-[0_10px_24px_rgba(242,106,52,0.35)] hover:scale-[1.03]"
                            : "bg-pb-ink text-white hover:bg-pb-coral"
                        }`}
                      >
                        <ShoppingBag className="h-4 w-4" />
                        {meta.requires_buddy ? "Customize & buy" : "View details & buy"}
                      </Link>
                    ) : (
                      <button
                        data-testid={`btn-buy-${p.id}`}
                        onClick={() => handleAdd(p)}
                        className={`w-full inline-flex items-center justify-center gap-2 rounded-full font-extrabold py-3.5 transition ${
                          isFeatured
                            ? "bg-pb-coral text-white shadow-[0_10px_24px_rgba(242,106,52,0.35)] hover:scale-[1.03]"
                            : "bg-pb-ink text-white hover:bg-pb-coral"
                        }`}
                      >
                        <ShoppingBag className="h-4 w-4" />
                        Add to cart
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* SECONDARY products — slimmer cards */}
        {secondaryProducts.length > 0 && (
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {secondaryProducts.map((p) => {
              const meta = PRODUCT_META[p.id] || {};
              return (
                <div
                  key={p.id}
                  data-testid={`card-product-${p.id}`}
                  className="rounded-[2rem] p-6 flex items-center gap-5 border-2 border-white shadow-[0_18px_40px_rgba(28,22,40,0.06)] hover:-translate-y-1 transition-all duration-300"
                  style={{ background: meta.bg || "#fff" }}
                >
                  <div
                    className="h-24 w-24 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white border-4 border-white shadow"
                    style={{ color: meta.accent }}
                  >
                    {p.id === "buddy_print" ? (
                      <Sparkles className="h-10 w-10" />
                    ) : (
                      <Plus className="h-10 w-10" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span
                      className="inline-block text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full text-pb-ink"
                      style={{ background: meta.accent || "#FFD54F" }}
                    >
                      {meta.badge}
                    </span>
                    <h3 className="font-display font-black text-xl mt-2 leading-tight">
                      {p.name} — ${p.amount}
                    </h3>
                    <p className="text-pb-soft mt-1 text-sm leading-relaxed">
                      {meta.headline}
                    </p>
                  </div>
                  <button
                    data-testid={`btn-buy-${p.id}`}
                    onClick={() => handleAdd(p)}
                    className="flex-shrink-0 inline-flex items-center gap-2 rounded-full bg-pb-ink text-white font-extrabold px-5 py-3 hover:bg-pb-coral transition whitespace-nowrap"
                  >
                    {meta.requires_buddy ? "Customize" : "Add"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-center text-xs text-pb-soft mt-10">
          Secure checkout powered by Stripe. Coupon code <strong>WELCOME10</strong> takes 10% off your first order.
        </p>
      </div>

      <ReservePreorderDialog open={preorderOpen} onOpenChange={setPreorderOpen} />
    </section>
  );
}

function CustomBuddyVisual() {
  // A whimsical "your buddy is yet to be created" visual — a magic wand summoning
  // a sparkly silhouette inside a thought bubble. Intentionally NOT one of the 6 official
  // Plate Buddies — communicates "you design your own."
  return (
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(ellipse at 30% 20%, #F3E5F5 0%, #ECE5F7 45%, #D6C7F0 100%)",
      }}
    >
      {/* Soft glow blobs */}
      <div
        aria-hidden
        className="absolute -top-8 -left-8 h-40 w-40 rounded-full opacity-60"
        style={{ background: "radial-gradient(circle, #FFD54F 0%, transparent 70%)", filter: "blur(20px)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-10 -right-10 h-44 w-44 rounded-full opacity-50"
        style={{ background: "radial-gradient(circle, #F26A34 0%, transparent 70%)", filter: "blur(20px)" }}
      />

      {/* Floating sparkles */}
      <Sparkles className="absolute top-6 left-6 h-5 w-5 text-pb-sun animate-pulse" strokeWidth={2.5} />
      <Sparkles className="absolute top-12 right-10 h-4 w-4 text-pb-coral animate-pulse" style={{ animationDelay: "0.5s" }} strokeWidth={2.5} />
      <Sparkles className="absolute bottom-8 left-12 h-3.5 w-3.5 text-[#4E2C80] animate-pulse" style={{ animationDelay: "0.8s" }} strokeWidth={2.5} />
      <Sparkles className="absolute bottom-14 right-6 h-4 w-4 text-pb-aqua animate-pulse" style={{ animationDelay: "1.1s" }} strokeWidth={2.5} />

      {/* Mystery buddy card — silhouette inside a sticker frame */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="relative bg-white rounded-3xl border-[5px] border-white shadow-[0_18px_44px_rgba(78,44,128,0.35)] rotate-[-4deg] w-[55%] aspect-square overflow-hidden"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, rgba(179,157,219,0.12) 0 8px, transparent 8px 16px)",
            backgroundColor: "#F3E5F5",
          }}
        >
          {/* Question-mark "buddy-shaped" silhouette */}
          <svg
            viewBox="0 0 100 100"
            className="absolute inset-0 w-full h-full p-[15%]"
            aria-hidden
          >
            <defs>
              <linearGradient id="buddyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#B39DDB" />
                <stop offset="100%" stopColor="#4E2C80" />
              </linearGradient>
            </defs>
            {/* Rounded buddy body silhouette */}
            <path
              d="M50 8 C25 8 12 28 12 50 C12 70 22 88 50 92 C78 88 88 70 88 50 C88 28 75 8 50 8 Z"
              fill="url(#buddyGrad)"
              opacity="0.9"
            />
            {/* Mystery question mark */}
            <text
              x="50"
              y="68"
              textAnchor="middle"
              fontSize="48"
              fontWeight="900"
              fill="#FFD54F"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              ?
            </text>
          </svg>

          {/* "Yours" tag at bottom */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-pb-sun text-pb-ink font-display font-black rotate-[-2deg] rounded-lg px-3 py-1 text-[10px] shadow border-2 border-white whitespace-nowrap">
            Yours · One-of-a-kind
          </div>
        </div>

        {/* Magic wand "creating" the buddy */}
        <div className="absolute right-[8%] top-[14%] rotate-[18deg]">
          <div className="relative bg-white rounded-full p-3 shadow-[0_10px_24px_rgba(242,106,52,0.4)] border-[3px] border-pb-sun">
            <Wand2 className="h-6 w-6 text-pb-coral" strokeWidth={2.5} />
          </div>
          {/* Trail of sparkles flowing toward the buddy */}
          <Sparkles className="absolute -bottom-6 -left-3 h-3 w-3 text-pb-sun" strokeWidth={3} />
          <Sparkles className="absolute -bottom-12 -left-8 h-2.5 w-2.5 text-pb-coral" strokeWidth={3} />
        </div>
      </div>
    </div>
  );
}

function BuddyPicker({ picked, setPicked }) {
  return (
    <div className="mt-5 bg-white rounded-2xl p-3 border-2 border-pb-sky/40">
      <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-soft mb-2">
        Pick your Buddy
      </p>
      <div className="grid grid-cols-6 gap-1.5">
        {PICKABLE_BUDDIES.map((b) => (
          <button
            key={b.id}
            type="button"
            data-testid={`buddy-pick-${b.id}`}
            onClick={() => setPicked(b.id)}
            aria-label={b.name}
            title={b.name}
            className={`relative h-10 w-10 rounded-full overflow-hidden border-[3px] transition ${
              picked === b.id
                ? "border-pb-coral scale-110 shadow-md"
                : "border-white opacity-60 hover:opacity-100"
            }`}
          >
            <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
            {picked === b.id && (
              <span className="absolute inset-0 ring-2 ring-pb-coral rounded-full" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function InteractiveSystemVisual() {
  // A clean, teal-tinted "coming soon" visual: a stylized plate with faint
  // Buddy silhouettes and a soft tech glow — hints at an app-connected device
  // without pretending we have real product photography yet.
  return (
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(ellipse at 50% 35%, #D6F4F8 0%, #E8F7FA 50%, #C6E7F0 100%)",
      }}
    >
      {/* Ambient glow blobs */}
      <div
        aria-hidden
        className="absolute -top-10 -left-8 h-40 w-40 rounded-full opacity-60"
        style={{ background: "radial-gradient(circle, #4DD0E1 0%, transparent 70%)", filter: "blur(22px)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-12 -right-10 h-44 w-44 rounded-full opacity-50"
        style={{ background: "radial-gradient(circle, #B39DDB 0%, transparent 70%)", filter: "blur(22px)" }}
      />

      {/* Floating sparkles */}
      <Sparkles className="absolute top-6 left-8 h-5 w-5 text-pb-aqua animate-pulse" strokeWidth={2.5} />
      <Sparkles className="absolute top-10 right-10 h-4 w-4 text-pb-coral animate-pulse" style={{ animationDelay: "0.4s" }} strokeWidth={2.5} />
      <Sparkles className="absolute bottom-10 left-10 h-3.5 w-3.5 text-[#4E2C80] animate-pulse" style={{ animationDelay: "0.9s" }} strokeWidth={2.5} />

      {/* Stylized interactive plate */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-[62%] aspect-square rounded-full bg-white border-[6px] border-white shadow-[0_18px_44px_rgba(28,22,40,0.18)] overflow-hidden">
          {/* Inner ring */}
          <div
            className="absolute inset-3 rounded-full"
            style={{
              background:
                "radial-gradient(circle at 50% 40%, #FFFFFF 0%, #F0FAFB 60%, #D6F4F8 100%)",
            }}
          />
          {/* Faint divider lines */}
          <div
            aria-hidden
            className="absolute inset-6 rounded-full border-2 border-dashed border-pb-aqua/40"
          />
          {/* Center chip-looking emblem */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative h-20 w-20 rounded-2xl bg-pb-ink text-pb-aqua flex items-center justify-center shadow-[0_10px_24px_rgba(28,22,40,0.35)]">
              <Wand2 className="h-9 w-9" strokeWidth={2.5} />
              <span className="absolute -top-2 -right-2 h-3 w-3 rounded-full bg-pb-coral animate-ping" />
              <span className="absolute -top-2 -right-2 h-3 w-3 rounded-full bg-pb-coral" />
            </div>
          </div>
          {/* "Coming soon" ribbon */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-pb-aqua text-pb-ink font-display font-black rotate-[-2deg] rounded-lg px-3 py-1 text-[10px] shadow border-2 border-white whitespace-nowrap uppercase tracking-widest">
            Coming soon
          </div>
        </div>
      </div>
    </div>
  );
}

function ReservePreorderDialog({ open, onOpenChange }) {
  const [parentName, setParentName] = useState("");
  const [email, setEmail] = useState("");
  const [kidsAges, setKidsAges] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { position } once reserved

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!parentName.trim() || !email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await reserveInteractiveSystem({
        parent_name: parentName.trim(),
        email: email.trim(),
        kids_ages: kidsAges.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      setSuccess({ position: res?.position });
      toast.success("You're on the list!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Reservation failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (next) => {
    if (!next) {
      // Reset on close so repeat opens feel fresh.
      setTimeout(() => {
        setParentName("");
        setEmail("");
        setKidsAges("");
        setNotes("");
        setSuccess(null);
      }, 250);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="dialog-reserve-interactive"
        className="sm:max-w-[520px] rounded-3xl border-4 border-pb-aqua bg-pb-cream"
      >
        <DialogHeader>
          <span className="inline-flex items-center gap-1 self-start text-[10px] font-extrabold uppercase tracking-widest bg-pb-aqua text-pb-ink px-3 py-1 rounded-full">
            <Sparkles className="h-3 w-3" /> Interactive System · Pre-order
          </span>
          <DialogTitle className="font-display font-black text-2xl md:text-3xl text-pb-ink leading-tight">
            Reserve your spot — $79.99
          </DialogTitle>
          <DialogDescription className="text-pb-soft text-sm md:text-base">
            No charge today. Reservers get first access, launch pricing, and a heads-up before anyone else.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div
            data-testid="preorder-success"
            className="rounded-2xl bg-white border-2 border-pb-aqua/60 p-5 text-center"
          >
            <div className="mx-auto h-12 w-12 rounded-full bg-pb-aqua/20 text-pb-aqua flex items-center justify-center mb-3">
              <Check className="h-6 w-6" />
            </div>
            <h3 className="font-display font-black text-xl text-pb-ink">You're #{success.position} on the list!</h3>
            <p className="text-pb-soft text-sm mt-2">
              We'll email you the second the Interactive System opens for its first batch of reservations.
            </p>
            <button
              type="button"
              onClick={() => handleClose(false)}
              className="mt-5 inline-flex items-center justify-center rounded-full bg-pb-ink text-white font-extrabold px-5 py-3 hover:bg-pb-coral transition"
              data-testid="preorder-success-close"
            >
              Got it
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="preorder-name" className="text-pb-ink font-bold">Parent's name</Label>
              <Input
                id="preorder-name"
                data-testid="preorder-input-name"
                placeholder="e.g. Sam"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                className="mt-1 bg-white"
                required
              />
            </div>
            <div>
              <Label htmlFor="preorder-email" className="text-pb-ink font-bold">Email</Label>
              <Input
                id="preorder-email"
                data-testid="preorder-input-email"
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 bg-white"
                required
              />
            </div>
            <div>
              <Label htmlFor="preorder-ages" className="text-pb-ink font-bold">Kids' ages <span className="text-pb-soft font-normal">(optional)</span></Label>
              <Input
                id="preorder-ages"
                data-testid="preorder-input-ages"
                placeholder="e.g. 3 and 6"
                value={kidsAges}
                onChange={(e) => setKidsAges(e.target.value)}
                className="mt-1 bg-white"
              />
            </div>
            <div>
              <Label htmlFor="preorder-notes" className="text-pb-ink font-bold">Anything we should know? <span className="text-pb-soft font-normal">(optional)</span></Label>
              <Textarea
                id="preorder-notes"
                data-testid="preorder-input-notes"
                placeholder="Tell us what you're excited about."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1 bg-white min-h-[80px]"
                maxLength={600}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="preorder-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold py-3.5 shadow-[0_10px_24px_rgba(242,106,52,0.35)] hover:scale-[1.02] transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Sparkles className="h-4 w-4" />
              {submitting ? "Reserving…" : "Reserve my spot"}
            </button>
            <p className="text-center text-[11px] text-pb-soft">
              No charge today. Cancel anytime — we only email when there's real news.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

